import { useEffect } from "react";

// Retry only autoplay surfaces; forceMuted=false preserves a user-controlled sound toggle.
export function useAutoplayRetry(videoRef, source, { enabled = true, forceMuted = true } = {}) {
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !enabled) return;
    let pending = false;
    const visible = () => {
      const bounds = video.getBoundingClientRect();
      return (
        !document.hidden &&
        bounds.width > 0 &&
        bounds.height > 0 &&
        bounds.bottom > 0 &&
        bounds.top < window.innerHeight &&
        bounds.right > 0 &&
        bounds.left < window.innerWidth
      );
    };

    if (forceMuted) {
      video.defaultMuted = true;
      video.muted = true;
    }
    video.playsInline = true;

    const play = (fromGesture = false) => {
      if (!visible() || !video.paused || (pending && !fromGesture)) return;
      pending = true;
      if (forceMuted) video.muted = true;
      // Call play synchronously during the gesture so embedded browsers can
      // use that user activation. A rejected autoplay can be retried later.
      video
        .play()
        .catch(() => {})
        .finally(() => {
          pending = false;
        });
    };
    const retry = () => play();
    const onGesture = () => play(true);

    const listeners = [
      [video, "canplay", retry],
      [document, "touchend", onGesture],
      [document, "click", onGesture],
      [document, "keydown", onGesture],
      [document, "visibilitychange", retry],
      [window, "pageshow", retry],
    ];
    listeners.forEach(([target, event, handler]) => {
      target.addEventListener(event, handler, { passive: true });
    });

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) retry();
    });
    observer.observe(video);
    retry();

    return () => {
      listeners.forEach(([target, event, handler]) => {
        target.removeEventListener(event, handler);
      });
      observer.disconnect();
    };
  }, [videoRef, source, enabled, forceMuted]);
}
